package koala.dom

import kampfire.model.ListChange
import kampfire.model.LiveList
import kampfire.model.MutableTap
import kampfire.model.ScrollState
import koala.modifier.*
import kotlinx.css.LinearDimension
import kotlinx.css.px
import web.dom.document
import web.events.Event
import web.events.SCROLL
import web.events.addEventListener
import web.html.HTMLDivElement
import web.html.HTMLElement
import kotlin.math.abs

/**
 * A scrolling column of [list], applying each change in place rather than rebuilding.
 *
 * Each item is a child view built by [content], sized as [expectedHeight] until it renders. [scrollState]
 * reports whether the scroll is within [scrollBoundaryMargin] of either end.
 */
fun <T, K> ViewScope.lazyColumn(
    list: LiveList<T, K>,
    mod: Modifier? = null,
    scrollState: MutableTap<ScrollState?>? = null,
    expectedHeight: LinearDimension = 60.px,
    scrollBoundaryMargin: Int = 400,
    hideBar: Boolean = false,
    content: ViewScope.(T) -> Unit
): HTMLDivElement {
    val views = mutableListOf<View>()

    val barMod = if (hideBar) ScrollbarWidthNone else null
    val container = div(modify(mod, OverflowYScroll, barMod)) { }

    fun HTMLElement.mountView(item: T) = mountChildView("lazyColumnItem", this) {
        content(item)
    }

    fun setScrollState() {
        val state = scrollState ?: return
        val offset = abs(container.scrollTop)
        val atStart = offset <= scrollBoundaryMargin
        val atEnd = container.scrollHeight - offset - container.clientHeight <= scrollBoundaryMargin
        state.set { ScrollState(atStart, atEnd) }
    }

    fun insert(index: Int, item: T) {
        val currentElement = views.getOrNull(index)?.mount
        val itemElement = document.createDiv {
            setAttribute(Attribute.ContainIntrinsicSize, "auto $expectedHeight")
            setAttribute(Attribute.ContentVisibility, "auto")
        }
        val view = itemElement.mountView(item)
        views.add(index, view)
        container.insertBefore(itemElement, currentElement)
    }

    fun insert(index: Int, items: List<T>) {
        items.forEachIndexed { itemIndex, item ->
            insert(index + itemIndex, item)
        }
    }

    fun replace(index: Int, item: T) {
        val currentView = views[index]
        currentView.dispose()
        currentView.mount.mountView(item)
    }

    fun remove(index: Int, count: Int) {
        repeat(count) {
            val currentView = views[index]
            currentView.dispose()
            currentView.mount.remove()
            views.removeAt(index)
        }
        setScrollState()
    }

    fun clear() {
        views.forEach {
            it.dispose()
        }
        views.clear()
        container.clear()
    }

    launchEffect {
        list.changeFlow.collect { change ->
            when (change) {
                ListChange.Clear -> clear()
                is ListChange.Insert<T> -> insert(change.index, change.items)
                is ListChange.Remove -> remove(change.index, change.count)
                is ListChange.Replace<T> -> replace(change.index, change.item)
            }
        }
    }

    scrollState?.let {
        container.addEventListener(Event.SCROLL, {
            setScrollState()
        })
    }

    return container
}

/** The scroll position of a [lazyColumn]. */
data class LazyColumnState(
    val scrollIndex: Int = 0,
)